// The device filter: read a board for the projects created on one kind of device.
//
// A row of toggles rather than a dropdown, and multi-select rather than single:
// the interesting questions are comparisons ("phones against laptops"), and the
// four buckets fit on one line, so making the reader open a menu to change one
// would cost a click for every comparison.
//
// NOTHING selected means EVERY device, which is the default every board opens
// on and the cohort its cheap precomputed lane is materialized for. Selecting
// all four is the same population by a slower route, so the control clears
// itself back to "All devices" instead — one selection, one meaning.
//
// `unknown` is offered on purpose. Most of the table has no device recorded (an
// import, an MCP turn, and everything created before migration 0183), so a
// control that quietly excluded those rows would let "desktop + mobile +
// tablet" read as the whole population when it is a slice of it.

import { DEVICE_FILTER_KEYS, deviceLabel } from "../lib/device";
import "./device-filter.css";

type Props = {
  /** The selection, in the caller's own state (URL or restored). Empty = all. */
  devices: readonly string[];
  onChange: (next: readonly string[]) => void;
};

export function DeviceFilter({ devices, onChange }: Props) {
  const toggle = (key: string) => {
    const next = devices.includes(key)
      ? devices.filter((value) => value !== key)
      : [...devices, key];
    // Everything ticked is the same cohort as nothing ticked. Collapsing to the
    // empty selection keeps the two from being different-looking spellings of
    // one answer — and keeps the cheaper unfiltered lane in play.
    onChange(next.length === DEVICE_FILTER_KEYS.length ? [] : next);
  };

  return (
    <div className="dv-filter">
      <span className="dv-filter__label">Device</span>
      <div className="dv-filter__options" role="group" aria-label="Device">
        <button
          type="button"
          className={"dv-filter__opt" + (devices.length === 0 ? " dv-filter__opt--on" : "")}
          aria-pressed={devices.length === 0}
          onClick={() => onChange([])}
        >
          All
        </button>
        {DEVICE_FILTER_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={"dv-filter__opt" + (devices.includes(key) ? " dv-filter__opt--on" : "")}
            aria-pressed={devices.includes(key)}
            onClick={() => toggle(key)}
          >
            {deviceLabel(key)}
          </button>
        ))}
      </div>
    </div>
  );
}
